"use client";

import { Button } from "@/03-presenters/components/ui/button";
import { Calendar } from "@/03-presenters/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/03-presenters/components/ui/popover";
import { Separator } from "@/03-presenters/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/03-presenters/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { format, subDays, parseISO, differenceInDays } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState, memo } from "react";
import { locales } from "@/i18n";
import { useLocale } from "next-intl";
import { useTableStore } from "../TableStoreProvider";
import { BEIJING_NOW_DATE_FUNC } from "@/00-shared/const";
import { countryStore } from "@/03-presenters/components/MultiCountrySelect";
import { cx } from "class-variance-authority";
export const TIME_JOINT_LABEL = "__timeJoint__";
const getStartDate = (x: string = "7") => {
  return urlDateFormat(subDays(BEIJING_NOW_DATE_FUNC(), Number(x)));
};
const stringToDate = (dateString: string): Date => {
  const year = parseInt(dateString.substring(0, 4), 10);
  const month = parseInt(dateString.substring(4, 6), 10) - 1;
  const day = parseInt(dateString.substring(6, 8), 10);
  return new Date(year, month, day);
};
const getLastXDay = (start_date_time: string) => {
  const parsedEndDate = stringToDate(start_date_time);
  const x = differenceInDays(BEIJING_NOW_DATE_FUNC(), parsedEndDate);
  return x.toString();
};
export const getOutPutDate = (url_start_date_time: string, url_end_date_time: string) => {
  return {
    from: url_start_date_time && url_end_date_time ? url_start_date_time : "",
    to: url_start_date_time && url_end_date_time ? url_end_date_time : "",
    lastXDay: url_start_date_time && !url_end_date_time ? getLastXDay(url_start_date_time) : ""
  };
};
const urlDateFormat = (date: Date) => format(date, "yyyyMMdd");
const useDateRange = (filterKey: string) => {
  const tableStore = useTableStore();
  const updateFilter = tableStore(state => state.updateFilter);
  const setPageIndex = tableStore(state => state.setPageIndex);
  const filterValue = tableStore(state => state.filters.find(filter => filter.filterKey === filterKey)?.value) || "";
  const [url_start_date_time, url_end_date_time] = filterValue?.split(TIME_JOINT_LABEL);
  const date = getOutPutDate(url_start_date_time, url_end_date_time);
  const setDateRange = ({
    from,
    to,
    lastXDay
  }: {
    from?: string;
    to?: string;
    lastXDay?: string;
  }) => {
    if (lastXDay) {
      updateFilter(filterKey, `${getStartDate(lastXDay)}${TIME_JOINT_LABEL}`);
      setPageIndex(0);
      return;
    }
    if (from && !to) {
      return;
    }
    if (!from && !to && !lastXDay) {
      updateFilter(filterKey, "");
      setPageIndex(0);
      return;
    }
    updateFilter(filterKey, `${from}${TIME_JOINT_LABEL}${to}`);
    setPageIndex(0);
  };
  const dateRange = {
    from: date.from,
    to: date.to,
    lastXDay: !date.lastXDay ? null : date.lastXDay ?? "7"
  };
  return {
    dateRange,
    setDateRange
  };
};
function DatePickerWithRange({
  locale,
  filterKey,
  displayFrom,
  setDisplayFrom
}: {
  locale: locales;
  filterKey: string;
  placeHolderPrefix?: string;
  displayFrom?: string;
  setDisplayFrom: Dispatch<SetStateAction<string | undefined>>;
}) {
  const {
    dateRange: date,
    setDateRange: setDate
  } = useDateRange(filterKey);
  const formatDate = (value: string) => {
    try {
      if (locale === "en") {
        return format(parseISO(value), "LLL dd, y");
      }
      return format(parseISO(value), "yyyy-MM-dd");
    } catch (e) {
      return "";
    }
  };
  const {
    to,
    lastXDay
  } = date;
  const parseDate = displayFrom && to ? `${formatDate(displayFrom)}-${formatDate(to)}` : displayFrom && `${formatDate(displayFrom)}-选择结束日期`;
  const displayContent = lastXDay && !displayFrom ? <span>选择日期</span> : parseDate ? parseDate : <span>选择日期</span>;
  return <div className="mr-2" data-sentry-component="DatePickerWithRange" data-sentry-source-file="CustomizedDatePickerV2.tsx">
      <Popover data-sentry-element="Popover" data-sentry-source-file="CustomizedDatePickerV2.tsx">
        <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="CustomizedDatePickerV2.tsx">
          <Button id="date" variant={"outline"} className={cn("h-8 min-w-[300px] justify-start text-left font-normal", !date && "text-muted-foreground")} data-sentry-element="Button" data-sentry-source-file="CustomizedDatePickerV2.tsx">
            <CalendarIcon className="mr-2 h-4 w-4" data-sentry-element="CalendarIcon" data-sentry-source-file="CustomizedDatePickerV2.tsx" />
            {displayContent}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" onPointerDownOutside={e => {
        if (displayFrom && !to) {
          setDisplayFrom("");
        }
      }} data-sentry-element="PopoverContent" data-sentry-source-file="CustomizedDatePickerV2.tsx">
          <Calendar initialFocus mode="range" selected={lastXDay && !displayFrom ? undefined : {
          from: displayFrom ? parseISO(displayFrom) : undefined,
          to: to ? parseISO(to) : undefined
        }} onSelect={range => {
          setDisplayFrom(range?.from ? urlDateFormat(range?.from) : "");
          setDate({
            from: range?.from ? urlDateFormat(range?.from) : "",
            to: range?.to ? urlDateFormat(range?.to) : ""
          });
        }} numberOfMonths={2} disabled={{
          after: new Date()
        }} data-sentry-element="Calendar" data-sentry-source-file="CustomizedDatePickerV2.tsx" />
        </PopoverContent>
      </Popover>
    </div>;
}
export default memo(function CustomizedDatePicker({
  locale,
  filterKey
}: {
  locale: locales;
  filterKey: string;
}) {
  const {
    setDateRange,
    dateRange: date
  } = useDateRange(filterKey);
  const [displayFrom, setDisplayFrom] = useState<string | undefined>();
  const countryCode = countryStore(state => state.countryCode);
  useEffect(() => {
    if (displayFrom === date.from) {
      return;
    }
    setDisplayFrom(date.from && !date.lastXDay ? date.from : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date.from, date.lastXDay]);
  return <div className="my-[6px] flex items-center gap-4">
      <ToggleGroup size="sm" className="rounded border border-border px-1" type="single" onValueChange={val => {
      setDisplayFrom("");
      setDateRange({
        lastXDay: val
      });
    }} value={date.lastXDay?.toString() ?? ""}>
        <ToggleGroupItem className="data-[state=on]:bg-none! hover:bg-none! h-8 font-normal" value="7" aria-label="Toggle bold">
          近 7 天
        </ToggleGroupItem>
        <Separator className="h-4" orientation="vertical" />
        <ToggleGroupItem className="data-[state=on]:bg-none! hover:bg-none! h-8 font-normal" value="14" aria-label="Toggle italic">
          近 14 天
        </ToggleGroupItem>
        <Separator className="h-4" orientation="vertical" />
        <ToggleGroupItem className="data-[state=on]:bg-none! hover:bg-none! h-8 font-normal" value="28" aria-label="Toggle underline">
          近 28 天
        </ToggleGroupItem>
        <Separator className="h-4" orientation="vertical" />
        <ToggleGroupItem className="data-[state=on]:bg-none! hover:bg-none! h-8 font-normal" value="90" aria-label="Toggle underline">
          近 90 天
        </ToggleGroupItem>
        <Separator className="h-4" orientation="vertical" />
        <ToggleGroupItem className="data-[state=on]:bg-none! hover:bg-none! h-8 font-normal" value="180" aria-label="Toggle underline">
          近 180 天
        </ToggleGroupItem>
        <Separator className={cx("h-4", countryCode !== "US" && "hidden")} orientation="vertical" />
        <ToggleGroupItem className={cx("data-[state=on]:bg-none! hover:bg-none! h-8 font-normal", countryCode !== "US" && "hidden")} value="365" aria-label="Toggle underline">
          近 365 天
        </ToggleGroupItem>
      </ToggleGroup>
      <DatePickerWithRange locale={locale} filterKey={filterKey} displayFrom={displayFrom} setDisplayFrom={setDisplayFrom} />
    </div>;
});